/**
 * Organization context: multi-select subset of the user's active org memberships.
 * Views read selectedOrgIds for data scoping; independent of JWT primary org (org_id).
 */
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { UserOrganization } from "../api/users";
import { SELECTED_ORG_IDS_KEY } from "./constants";
import type { OrgContextOrganization } from "./types";
import {
  readStoredOrgIds,
  reconcileSelectedOrgIds,
  toOrgContextOrganization,
} from "./utils";

// Re-export so callers can stage IDs before refreshUser()
export { stageAdditionalOrgIds } from "./utils";

export interface OrganizationContextValue {
  availableOrgs: OrgContextOrganization[];
  selectedOrgIds: string[];
  selectedOrgs: OrgContextOrganization[];
  isAllSelected: boolean;
  hasOrganizationMembership: boolean;
  hiddenOrgCount: number;
  toggleOrg: (orgId: string) => void;
  setSelectedOrgIds: (ids: string[]) => void;
  isOrgSelected: (orgId: string) => boolean;
}

const OrganizationContext = React.createContext<OrganizationContextValue | null>(
  null,
);

function persistOrgIds(ids: string[]) {
  try {
    sessionStorage.setItem(SELECTED_ORG_IDS_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

export function OrganizationContextProvider({
  organizations,
  hasOrganizationMembership,
  children,
}: {
  organizations: UserOrganization[];
  hasOrganizationMembership: boolean;
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const availableOrgs = React.useMemo(
    () => organizations.map(toOrgContextOrganization),
    [organizations],
  );
  const availableIds = React.useMemo(
    () => availableOrgs.map((o) => o.id),
    [availableOrgs],
  );

  const [selectedOrgIds, setSelectedOrgIdsState] = React.useState<string[]>(
    () => {
      if (!hasOrganizationMembership) return [];
      const stored = readStoredOrgIds(availableIds);
      return reconcileSelectedOrgIds(stored, availableIds);
    },
  );

  React.useEffect(() => {
    if (!hasOrganizationMembership) {
      setSelectedOrgIdsState([]);
      return;
    }
    setSelectedOrgIdsState((prev) => {
      // Re-read sessionStorage so externally staged IDs (e.g. from bulk operations
      // or mock data generation via stageAdditionalOrgIds) are respected when
      // the available org list changes.
      const stored = readStoredOrgIds(availableIds);
      const base = stored ?? (prev.length ? prev : null);
      const next = reconcileSelectedOrgIds(base, availableIds);
      persistOrgIds(next);
      return next;
    });
  }, [availableIds.join(","), hasOrganizationMembership]);

  const invalidateScopedLists = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["admin-users", "list"] });
    void queryClient.invalidateQueries({ queryKey: ["organizations", "list"] });
  }, [queryClient]);

  const setSelectedOrgIds = React.useCallback(
    (ids: string[]) => {
      if (!hasOrganizationMembership) return;
      const valid = ids.filter((id) => availableIds.includes(id));
      if (valid.length === 0) return;
      setSelectedOrgIdsState(valid);
      persistOrgIds(valid);
      invalidateScopedLists();
    },
    [availableIds, hasOrganizationMembership, invalidateScopedLists],
  );

  const toggleOrg = React.useCallback(
    (orgId: string) => {
      if (!availableIds.includes(orgId)) return;
      setSelectedOrgIdsState((prev) => {
        const isSelected = prev.includes(orgId);
        if (isSelected && prev.length <= 1) return prev;
        const next = isSelected
          ? prev.filter((id) => id !== orgId)
          : [...prev, orgId];
        persistOrgIds(next);
        invalidateScopedLists();
        return next;
      });
    },
    [availableIds, invalidateScopedLists],
  );

  const selectedOrgs = React.useMemo(
    () => availableOrgs.filter((o) => selectedOrgIds.includes(o.id)),
    [availableOrgs, selectedOrgIds],
  );

  const value = React.useMemo<OrganizationContextValue>(
    () => ({
      availableOrgs,
      selectedOrgIds,
      selectedOrgs,
      isAllSelected:
        availableOrgs.length > 0 &&
        selectedOrgIds.length === availableOrgs.length,
      hasOrganizationMembership,
      hiddenOrgCount: Math.max(0, availableOrgs.length - selectedOrgIds.length),
      toggleOrg,
      setSelectedOrgIds,
      isOrgSelected: (orgId: string) => selectedOrgIds.includes(orgId),
    }),
    [
      availableOrgs,
      selectedOrgIds,
      selectedOrgs,
      hasOrganizationMembership,
      toggleOrg,
      setSelectedOrgIds,
    ],
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganizationContext(): OrganizationContextValue {
  const ctx = React.useContext(OrganizationContext);
  if (!ctx) {
    throw new Error(
      "useOrganizationContext must be used within OrganizationContextProvider",
    );
  }
  return ctx;
}

/** Safe when provider may be absent (e.g. auth loading). */
export function useOrganizationContextOptional(): OrganizationContextValue | null {
  return React.useContext(OrganizationContext);
}
